import axios from "axios";
import i18n from "i18next";
import {
  clearStoredAuthTokens,
  getStoredAccessToken,
} from "@/services/authTokenStorage";
import { refreshStoredAccessToken } from "@/services/authRefreshSession";
import { extractErrorMessage } from "@/services/apiErrors";
import { notifyApiError } from "@/services/apiErrorNotifications";
import {
  carriesAuthHeader,
  createBearerAuthHeaders,
  JSON_HTTP_HEADERS,
} from "@/services/httpHeaders";
import type { AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";

type UnauthorizedHandler = () => void;
type RetryConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
  _sessionToken?: string;
};

let unauthorizedHandler: UnauthorizedHandler | null = null;

/** Registers a callback invoked when the session is terminated. */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null) {
  unauthorizedHandler = handler;
}

export const api = axios.create({
  baseURL: "/",
  headers: JSON_HTTP_HEADERS,
});

function clearSession() {
  if (!getStoredAccessToken()) return;
  clearStoredAuthTokens();
  unauthorizedHandler?.();
  notifyApiError(i18n.t("common.sessionExpired"));
}

api.interceptors.request.use((config) => {
  // A request signed already speaks for a project the session is not signed
  // into, so the session token never overrides it.
  if (carriesAuthHeader(config.headers)) return config;

  const token = getStoredAccessToken();
  if (token) {
    (config as RetryConfig)._sessionToken = token;
    config.headers = config.headers ?? {};
    Object.assign(config.headers, createBearerAuthHeaders(token));
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!axios.isAxiosError(error)) return Promise.reject(error);

    const status = error.response?.status;
    const original = error.config as RetryConfig | undefined;
    if (status === 401 && original?._sessionToken) {
      if (original.signal?.aborted) return Promise.reject(error);

      const currentToken = getStoredAccessToken();
      // A late response for an older token can use the already refreshed one.
      if (!original._retry && currentToken) {
        original._retry = true;
        const newToken = currentToken !== original._sessionToken
          ? currentToken
          : await refreshStoredAccessToken();
        if (original.signal?.aborted) return Promise.reject(error);
        if (newToken) {
          original._sessionToken = newToken;
          Object.assign(original.headers, createBearerAuthHeaders(newToken));
          return api.request(original as AxiosRequestConfig);
        }
      }
      if (getStoredAccessToken() === original._sessionToken) clearSession();
      return Promise.reject(error);
    }

    if (status && status >= 400 && status < 600) {
      notifyApiError(extractErrorMessage(error, error.message));
    }
    return Promise.reject(error);
  },
);
